import type * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export type TabItem = { value: string; label: React.ReactNode };
export type TabsProps = {
  tabs: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
};

/** Segmented tabs with the sliding active-pill indicator (240ms ease-standard). */
export function Tabs({ tabs, value, defaultValue, onValueChange, className = "" }: TabsProps) {
  const [internal, setInternal] = useState(defaultValue ?? tabs[0]?.value ?? "");
  const current = value ?? internal;
  const select = (v: string) => {
    if (value === undefined) setInternal(v);
    onValueChange?.(v);
  };
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState({ x: 0, width: 0 });
  const [initialized, setInitialized] = useState(false);
  const measure = useCallback(() => {
    const active = triggerRefs.current[current];
    if (!active) return;
    setIndicator({ x: active.offsetLeft, width: active.offsetWidth });
  }, [current]);
  useLayoutEffect(() => {
    measure();
    const raf = requestAnimationFrame(() => setInitialized(true));
    return () => cancelAnimationFrame(raf);
  }, [measure]);
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);
  return (
    <div className={["pr-tabs__list", className].filter(Boolean).join(" ")} role="tablist">
      <div
        className="pr-tabs__indicator"
        data-init={initialized}
        style={{ transform: `translateX(${indicator.x}px)`, width: indicator.width }}
        aria-hidden="true"
      />
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          aria-selected={t.value === current}
          data-active={t.value === current}
          className="pr-tabs__trigger"
          ref={(el) => {
            triggerRefs.current[t.value] = el;
          }}
          onClick={() => select(t.value)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}
